// CSV export for the cross-region comparison table.
//
// rowsToCsv() is pure and side-effect free so it can be tested in isolation.
// downloadCsv() is the thin browser-side trigger that should not be tested.

/**
 * CSV-escape a single cell value: wrap in double quotes and escape any
 * embedded double quotes by doubling them. Handles null, undefined, and
 * numeric values safely.
 * @param {any} value
 * @returns {string}
 */
export function csvCell(value) {
  if (value === null || value === undefined) return '';
  const str = String(value);
  // Only quote if the value contains a comma, newline, or double-quote.
  if (/[",\n\r]/.test(str)) {
    return '"' + str.replace(/"/g, '""') + '"';
  }
  return str;
}

// Column definitions: [header label, row accessor]
const COLUMNS = [
  ['Country Code', (r) => r.countryCode],
  ['Country', (r) => r.countryName],
  ['Available', (r) => (r.available ? 'yes' : 'no')],
  ['App Name', (r) => r.name],
  ['Seller', (r) => r.sellerName],
  ['Rating', (r) => r.averageUserRating],
  ['Rating Count', (r) => r.userRatingCount],
  ['Price', (r) => r.price],
  ['Currency', (r) => r.currency],
  ['Formatted Price', (r) => r.formattedPrice],
  ['Chart Free', (r) => r.chartRankFree],
  ['Chart Paid', (r) => r.chartRankPaid],
  ['Chart Grossing', (r) => r.chartRankGrossing],
  ['Chart Label', (r) => r.chartLabel],
  ['Store URL', (r) => r.storeUrl],
];

const CHART_ENTRY_COLUMNS = [
  ['Rank', (entry) => entry.rank],
  ['Name', (entry) => entry.name],
  ['Artist', (entry) => entry.artistName],
  ['Genre', (entry) => entry.primaryGenre],
  ['Track ID', (entry) => entry.trackId],
  ['App Store URL', (entry) => chartEntryStoreUrl(entry)],
];

/**
 * Convert an array of RegionRows into a UTF-8 CSV string.
 * @param {import('./normalizeApp.js').RegionRow[]} rows
 * @returns {string}
 */
export function rowsToCsv(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return '';

  const header = COLUMNS.map(([label]) => csvCell(label)).join(',');
  const lines = rows.map((row) =>
    COLUMNS.map(([, get]) => csvCell(get(row))).join(','),
  );

  return [header, ...lines].join('\r\n');
}

/**
 * Convert Chart Browser entries into a UTF-8 CSV string. The caller passes the
 * entries in the currently displayed order, so the CSV mirrors active sort and
 * filter state without doing any network work.
 * @param {import('./chartRank.js').ChartBrowserEntry[]} entries
 * @returns {string}
 */
export function chartEntriesToCsvRows(entries) {
  if (!Array.isArray(entries) || entries.length === 0) return '';

  const header = CHART_ENTRY_COLUMNS.map(([label]) => csvCell(label)).join(',');
  const lines = entries.map((entry) =>
    CHART_ENTRY_COLUMNS.map(([, get]) => csvCell(get(entry))).join(','),
  );

  return [header, ...lines].join('\r\n');
}

/**
 * Build a direct App Store URL from the chart entry. RSS entries do not need
 * another lookup for this; the numeric track id is enough for a stable link.
 * @param {import('./chartRank.js').ChartBrowserEntry} entry
 * @returns {string|null}
 */
function chartEntryStoreUrl(entry) {
  const explicitUrl = entry?.storeUrl ?? entry?.url;
  if (typeof explicitUrl === 'string' && explicitUrl.trim()) return explicitUrl.trim();

  const trackId = entry?.trackId;
  if (trackId === null || trackId === undefined || trackId === '') return null;
  return `https://apps.apple.com/app/id${trackId}`;
}

/**
 * Trigger a browser download of a CSV string.
 * @param {string} csv
 * @param {string} filename
 */
export function downloadCsv(csv, filename) {
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Build the CSV filename for a given app name and date string.
 * @param {string|null} appName
 * @param {string} dateStr - e.g. "2026-06-16"
 * @returns {string}
 */
export function buildCsvFilename(appName, dateStr) {
  const safe = (appName ?? 'app')
    .replace(/[^a-zA-Z0-9぀-ヿ一-鿿]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40) || 'app';
  return `app-radar-${safe}-${dateStr}.csv`;
}

/**
 * Build the CSV filename for a Chart Browser export.
 * @param {string|null|undefined} country - storefront code, e.g. "us".
 * @param {string|null|undefined} type - chart type, e.g. "top-free".
 * @param {string} dateStr - e.g. "2026-06-18".
 * @returns {string}
 */
export function buildChartCsvFilename(country, type, dateStr) {
  const slug = (value, fallback) => {
    const cleaned = String(value ?? '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    return cleaned || fallback;
  };
  return `app-radar-chart-${slug(country, 'xx')}-${slug(type, 'chart')}-${dateStr}.csv`;
}
